// useLiveTracking — production live GPS pipeline for officers in the field.
//
// Responsibilities:
// - Acquire and watch geolocation with explicit permission handling
// - Apply per-profile min interval / min distance / battery throttling
// - Batch points into officer_live_locations (latest position upsert) and
//   patrol_track_points (history) with a single round-trip per flush
// - Enqueue points to localStorage when offline and replay on reconnect
// - Emit a structured status object for UI ("Live", "Searching", "Denied",
//   "Offline + N queued", etc.)
// - Cleanly tear down on unmount and on auth/org change
//
// Background-safe note: browsers throttle JS in backgrounded tabs. Native
// background tracking requires a Cordova/Capacitor wrapper — out of scope
// here. We instead rely on the OS keeping the page alive via the PWA shell
// and flush whenever visibility resumes.
import { useEffect, useRef, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./auth";
import {
  getTrackingProfile, useTrackingProfile, TRACKING_PROFILES,
  type TrackingProfile,
} from "./tracking-prefs";

const QUEUE_KEY = "mr.gpsQueue.v1";

export type PermissionState =
  | "unknown" | "prompt" | "granted" | "denied"
  | "unsupported" | "restricted" | "timeout" | "unavailable";

export type LiveTrackingStatus = {
  permission: PermissionState;
  watching: boolean;
  online: boolean;
  lastError: string | null;
  lastFlushAt: string | null;
  lastPointAt: string | null;
  queuedCount: number;
  batteryPct: number | null;
  profile: TrackingProfile;
  effectiveProfile: TrackingProfile;
};

type Point = {
  latitude: number;
  longitude: number;
  accuracy_m: number | null;
  speed_mps: number | null;
  heading: number | null;
  recorded_at: string;
  battery_pct: number | null;
};

export type UseLiveTrackingOptions = {
  /** Active patrol/track id. When null, only officer_live_locations is updated. */
  patrolId?: string | null;
  trackId?: string | null;
  siteId?: string | null;
  /** Override the user-configured profile (e.g. when a high-priority dispatch is open). */
  profileOverride?: TrackingProfile;
  /** Don't actually start watching; useful for "armed" UI state. */
  enabled?: boolean;
};

function loadQueue(): Point[] {
  if (typeof window === "undefined") return [];
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY) ?? "[]"); }
  catch { return []; }
}
function saveQueue(q: Point[]) {
  if (typeof window === "undefined") return;
  // cap to last 1000 points to bound storage
  const tail = q.slice(-1000);
  localStorage.setItem(QUEUE_KEY, JSON.stringify(tail));
}

function haversineMeters(a: Point, b: Point): number {
  const R = 6_371_000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.latitude - a.latitude);
  const dLng = toRad(b.longitude - a.longitude);
  const lat1 = toRad(a.latitude), lat2 = toRad(b.latitude);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

async function readBattery(): Promise<{ level: number | null; charging: boolean | null }> {
  try {
    const nav = navigator as unknown as { getBattery?: () => Promise<{ level: number; charging: boolean }> };
    if (!nav.getBattery) return { level: null, charging: null };
    const b = await nav.getBattery();
    return { level: Math.round(b.level * 100), charging: b.charging };
  } catch { return { level: null, charging: null }; }
}

export function useLiveTracking(opts: UseLiveTrackingOptions = {}) {
  const { user, currentOrg } = useAuth();
  const [profile] = useTrackingProfile();
  const enabled = opts.enabled ?? true;

  const [status, setStatus] = useState<LiveTrackingStatus>(() => ({
    permission: "unknown",
    watching: false,
    online: typeof navigator === "undefined" ? true : navigator.onLine,
    lastError: null,
    lastFlushAt: null,
    lastPointAt: null,
    queuedCount: loadQueue().length,
    batteryPct: null,
    profile,
    effectiveProfile: profile,
  }));

  const watchIdRef = useRef<number | null>(null);
  const batchRef = useRef<Point[]>([]);
  const lastPointRef = useRef<Point | null>(null);
  const flushTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const batteryRef = useRef<{ level: number | null; charging: boolean | null }>({ level: null, charging: null });
  const stopRef = useRef<boolean>(false);

  const mergeStatus = useCallback((patch: Partial<LiveTrackingStatus>) => {
    setStatus((s) => ({ ...s, ...patch }));
  }, []);

  const effectiveProfile = useCallback((): TrackingProfile => {
    if (opts.profileOverride) return opts.profileOverride;
    const cur = getTrackingProfile();
    const cfg = TRACKING_PROFILES[cur];
    const lvl = batteryRef.current.level;
    if (lvl != null && lvl <= cfg.lowBatteryFloorPct && batteryRef.current.charging === false) {
      return "battery_saver";
    }
    return cur;
  }, [opts.profileOverride]);

  const flush = useCallback(async () => {
    if (!user || !currentOrg) return;
    const queue = [...loadQueue(), ...batchRef.current];
    if (queue.length === 0) return;
    batchRef.current = [];
    saveQueue(queue);

    if (typeof navigator !== "undefined" && !navigator.onLine) {
      mergeStatus({ queuedCount: queue.length });
      return;
    }

    const latest = queue[queue.length - 1];
    const orgId = currentOrg.id;

    try {
      // 1) upsert latest position (one row per officer for live ops view)
      const { error: liveErr } = await supabase
        .from("officer_live_locations")
        .upsert({
          officer_id: user.id,
          organization_id: orgId,
          patrol_id: opts.patrolId ?? null,
          site_id: opts.siteId ?? null,
          latitude: latest.latitude,
          longitude: latest.longitude,
          accuracy_m: latest.accuracy_m,
          heading: latest.heading,
          speed_mps: latest.speed_mps,
          battery_pct: latest.battery_pct,
          source: "browser",
          status: "active",
          recorded_at: latest.recorded_at,
        }, { onConflict: "officer_id" });
      if (liveErr) throw liveErr;

      // 2) append history if a track is active
      if (opts.trackId) {
        const rows = queue.map((p) => ({
          track_id: opts.trackId!,
          officer_id: user.id,
          organization_id: orgId,
          latitude: p.latitude,
          longitude: p.longitude,
          accuracy_m: p.accuracy_m,
          heading: p.heading,
          speed_mps: p.speed_mps,
          battery_pct: p.battery_pct,
          source: "browser",
          recorded_at: p.recorded_at,
          heartbeat_ok: true,
        }));
        const { error: histErr } = await supabase.from("patrol_track_points").insert(rows);
        if (histErr) throw histErr;
      }

      saveQueue([]);
      mergeStatus({
        queuedCount: 0,
        lastFlushAt: new Date().toISOString(),
        lastError: null,
      });
    } catch (e) {
      // keep queue for retry, surface error
      mergeStatus({
        queuedCount: queue.length,
        lastError: e instanceof Error ? e.message : String(e),
      });
    }
  }, [user, currentOrg, opts.patrolId, opts.trackId, opts.siteId, mergeStatus]);

  const acceptPoint = useCallback((pos: GeolocationPosition, force = false) => {
    const cfg = TRACKING_PROFILES[effectiveProfile()];
    const now = pos.timestamp ? new Date(pos.timestamp).toISOString() : new Date().toISOString();
    const next: Point = {
      latitude: pos.coords.latitude,
      longitude: pos.coords.longitude,
      accuracy_m: pos.coords.accuracy ?? null,
      speed_mps: pos.coords.speed ?? null,
      heading: pos.coords.heading ?? null,
      recorded_at: now,
      battery_pct: batteryRef.current.level,
    };
    const prev = lastPointRef.current;
    if (prev && !force) {
      const dt = new Date(next.recorded_at).getTime() - new Date(prev.recorded_at).getTime();
      const distance = haversineMeters(prev, next);
      if (dt < cfg.minIntervalMs && distance < cfg.minDistanceM) return;
    }
    lastPointRef.current = next;
    batchRef.current.push(next);
    mergeStatus({
      lastPointAt: next.recorded_at,
      effectiveProfile: effectiveProfile(),
      batteryPct: batteryRef.current.level,
    });
    if (force || batchRef.current.length >= cfg.batchSize) {
      void flush();
    }
  }, [effectiveProfile, flush, mergeStatus]);

  /** Restart the watch after a resume — set by the permission effect below. */
  const beginWatchRef = useRef<null | (() => void)>(null);
  const lastResumeRef = useRef(0);
  const permissionRef = useRef<PermissionState>("unknown");
  permissionRef.current = status.permission;

  // Permission lifecycle.
  useEffect(() => {
    if (!enabled) return;
    if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
      mergeStatus({ permission: "unsupported" });
      return;
    }

    let cancelled = false;

    // Start (or restart) the position watch. Reuses an already granted OS
    // permission; it is never called when permission is known-denied, so the
    // guard is not re-prompted every time the app comes back to the front.
    const beginWatch = () => {
      if (cancelled) return;
      if (watchIdRef.current != null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      const cfg = TRACKING_PROFILES[effectiveProfile()];
      stopRef.current = false;
      watchIdRef.current = navigator.geolocation.watchPosition(
        (pos) => {
          if (stopRef.current) return;
          mergeStatus({ permission: "granted", watching: true, lastError: null });
          acceptPoint(pos);
        },
        (err) => {
          let p: PermissionState = "denied";
          if (err.code === err.PERMISSION_DENIED) p = "denied";
          else if (err.code === err.POSITION_UNAVAILABLE) p = "unavailable";
          else if (err.code === err.TIMEOUT) p = "timeout";
          mergeStatus({ permission: p, watching: false, lastError: err.message });
        },
        {
          enableHighAccuracy: cfg.highAccuracy,
          maximumAge: Math.max(0, cfg.minIntervalMs - 1000),
          timeout: 30_000,
        },
      );
      mergeStatus({ watching: true });
    };
    beginWatchRef.current = beginWatch;

    const start = async () => {
      // prime the battery sensor (best-effort)
      batteryRef.current = await readBattery();

      // Inspect the existing permission state before asking for anything, so
      // an already-granted permission is simply reused and a denied one is not
      // re-triggered on every foreground.
      const pApi = (navigator as unknown as { permissions?: { query: (q: { name: string }) => Promise<{ state: PermissionState; onchange: ((e: Event) => void) | null }> } }).permissions;
      let known: PermissionState | null = null;
      if (pApi?.query) {
        try {
          const res = await pApi.query({ name: "geolocation" });
          known = res.state;
          if (!cancelled) mergeStatus({ permission: res.state });
          res.onchange = () => {
            mergeStatus({ permission: res.state });
            // Re-granted at OS level while the app is open — resume without a prompt.
            if (res.state === "granted") beginWatchRef.current?.();
          };
        } catch { /* Safari: ignore */ }
      }
      if (cancelled) return;
      if (known === "denied") {
        // Denied at the OS/browser level: stand down and let the existing
        // permission guidance handle it instead of prompting repeatedly.
        mergeStatus({ watching: false });
        return;
      }
      beginWatch();
    };
    void start();

    return () => {
      cancelled = true;
      stopRef.current = true;
      beginWatchRef.current = null;
      if (watchIdRef.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      watchIdRef.current = null;
      mergeStatus({ watching: false });
    };
  }, [enabled, profile, opts.profileOverride, acceptPoint, effectiveProfile, mergeStatus]);

  // Foreground resume. Browsers freeze or throttle a backgrounded tab, so when
  // the app becomes visible again (screen unlocked, app switched back, tab
  // re-activated) the watch is restarted and one fresh fix is requested so the
  // officer flips from "last known" back to "live". No prompt is shown when
  // permission is already granted, and nothing here starts a second tracking
  // session — the same pipeline is reused.
  useEffect(() => {
    if (!enabled) return;
    if (typeof document === "undefined") return;

    const resume = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastResumeRef.current < 10_000) return; // no aggressive polling
      lastResumeRef.current = now;
      void flush();
      const p = permissionRef.current;
      if (p === "denied" || p === "unsupported") return;
      beginWatchRef.current?.();
      if (!("geolocation" in navigator)) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          mergeStatus({ permission: "granted", watching: true, lastError: null });
          acceptPoint(pos, true);
        },
        (err) => {
          let ps: PermissionState | null = null;
          if (err.code === err.PERMISSION_DENIED) ps = "denied";
          else if (err.code === err.POSITION_UNAVAILABLE) ps = "unavailable";
          else if (err.code === err.TIMEOUT) ps = "timeout";
          mergeStatus({ ...(ps ? { permission: ps } : {}), lastError: err.message });
        },
        { enableHighAccuracy: true, maximumAge: 0, timeout: 20_000 },
      );
    };

    document.addEventListener("visibilitychange", resume);
    window.addEventListener("focus", resume);
    window.addEventListener("pageshow", resume);
    return () => {
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("focus", resume);
      window.removeEventListener("pageshow", resume);
    };
  }, [enabled, flush, acceptPoint, mergeStatus]);

  // Periodic flush + connectivity wiring.
  useEffect(() => {
    if (!enabled) return;
    const cfg = TRACKING_PROFILES[effectiveProfile()];
    flushTimerRef.current = setInterval(() => { void flush(); }, cfg.flushIntervalMs);

    const onOnline = () => { mergeStatus({ online: true }); void flush(); };
    const onOffline = () => mergeStatus({ online: false });

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    return () => {
      if (flushTimerRef.current) clearInterval(flushTimerRef.current);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      // final best-effort flush
      void flush();
    };
  }, [enabled, profile, opts.profileOverride, flush, effectiveProfile, mergeStatus]);

  return {
    status,
    flushNow: flush,
  };
}
